<template>
    <div
        class="o-path-settings"
        :style="componentStyles"
    >
        <header class="o-path-settings__header">
            <AIconButton
                :text="$t('ui.back')"
                :icon="componentParams.back_icon ||  'arrow-left-transparent'"
                :class="`back-button ${componentParams.is_show_mobile ? 'back-button--desktop' : ''} ${componentParams.back_button_class || ''}`"
                @onClick="onClickBack"
            />
            <AIconButton
                v-if="componentParams.is_show_mobile"
                :text="$t('ui.back')"
                icon="arrow-left-otg-green"
                class="back-button back-button--mobile"
                @onClick="onClickBack"
            />

            <div 
                class="o-path-settings__logo"
                v-if="componentParams.is_show_logo"
            >
                <div class="o-path-settings__logo-image" />
            </div>

            <OIntegrationCloseButton
                v-if="isShowCloseButton"
            />
            <ACloseIcon />
            <!-- <AIconButton
                icon="close-otg-black"
                class="close-button"
                @onClick="onClickBack(true)"
            />-->
        </header>
        <section class="path-settings-main-section">
            <h3 class="path-settings-title">
                {{ $t('ui.find-out-at-the-center', {mapName: t(map, 'name')}) }}
            </h3>
            <component
                :is="startItemsComponent"
                :startObjectId="startObjectId"
                @onChangeStart="onChangeStart"
            />
            <OQRScannerButton
                v-if="isQRScannerAvailable"
                class="qr-scanner-button"
                @onClick="onRunQRScanner"
            />
            <ODropdownOtg
                :items="destinationItems"
                :label="$t('ui.to')"
                :value="destinationObjectId"
                :disabled="true"
                class="path-settings-dropdown path-settings-dropdown--default"
                @input="onChangeDestination"
            />
            <ACheckbox
                v-if="!componentParams.is_hide_checkbox"
                v-model="guideModeCheckboxState"
                :label="$t('ui.show-route-for-wheelchair-and-pram')"
                class="path-settings-checkbox"
            />
        </section>
        <footer class="path-settings-footer">
            <AButtonOtg
                v-if="!componentParams.is_hide_checkbox"
                :title="$t('ui.directions')"
                :disabled="isDisabledStart"
                theme="primary"
                class="path-settings-footer-button"
                @onClick="onStartNavigation"
            />
            <div
                v-if="componentParams.is_show_footer_buttons"
                class="path-settings-footer__buttons"
            >
                <div
                    class="path-settings-footer__buttons-title"
                >
                    {{$t('ui.start_on_map')}}
                </div>
                <div class="path-settings-footer__buttons-wrapper">
                    <AButtonOtg
                        :title="$t('ui.popup.fastest_route')"
                        :disabled="isDisabledStart"
                        class="path-settings-footer__buttons-btn path-settings-footer__buttons-btn--fast"
                        @onClick="onStartNavigation(fastGuideMod)"
                    />
                    <AButtonOtg
                        :title="$t('ui.popup.easy_route')"
                        :disabled="isDisabledStart"
                        class="path-settings-footer__buttons-btn path-settings-footer__buttons-btn--easy"
                        @onClick="onStartNavigation(easyGuideMod)"
                    />
                </div>
            </div>
        </footer>
        <OQrReader
            v-if="isQRScanner"
            class="qr-scanner"
            @onClickBack="onCloseQRScanner"
        >
            <template #title>
                <span class="scan-qr-title">
                    {{ TRANSLATIONS.SCAN_QR_START_POSITION }}
                </span>
            </template>
        </OQrReader>
    </div>
</template>

<script>
import {
    mapState,
    mapGetters
} from "vuex";
import cloneDeep from 'lodash/cloneDeep'
import { isMobileOnly } from 'mobile-device-detect';

import ACheckbox from '../../atoms/a-checkbox';
import AButtonOtg from '../../atoms/a-button-otg';
import ACloseIcon from '../../atoms/a-close-icon';
import OQrReader from '../../organisms/o-qr-reader';
import AIconButton from '../../atoms/a-icon-button';
import ODropdownOtg from '../../organisms/o-dropdown-otg';
import ODropdownSelect from '../../organisms/o-dropdown-select';
import OQRScannerButton from '../../organisms/o-qr-scanner-button';
import OStartItemsDropdown from '../../organisms/o-start-items-dropdown';
import OIntegrationCloseButton from '../../organisms/o-integration-close-button';

import { routerPushHandler } from "@/utils/helpers/common";
import {
    getPointRouterParams
} from "./path-settings-service";
import {
    FAST,
    EASY
} from "@/utils/constants/app-constants";
import {WEB_INTEGRATION} from "@/utils/constants/route-constants";
import {OBJECT_INFO_CLOSE_CLICK} from "@/utils/constants/bus-events-constants";
import { GUIDE_NEW_POINT } from "../../../utils/constants/bus-events-constants";

export default {
    name: 'OPathSettings',
    components: {
        ACheckbox,
        OQrReader,
        ACloseIcon,
        AButtonOtg,
        AIconButton,
        ODropdownOtg,
        ODropdownSelect,
        OQRScannerButton,
        OStartItemsDropdown,
        OIntegrationCloseButton
    },
    data() {
        return {
            isQRScanner: false,
            guideModeCheckboxState: false,
            easyGuideMod: EASY,
            fastGuideMod: FAST
        }
    },
    computed: {
        TRANSLATIONS() {
            return {
                SCAN_QR_START_POSITION: this.$t('ui.scan-qr-start-position')
            }
        },
        ...mapState({
            map: state => state.map
        }),
        ...mapGetters([
            't',
            'getTenantsAZ',
            'getMyPosition',
            'getEntrancesAZ',
            'getMapConfigProp',
            'getServicesObjectsAZ',
            'getPortalsForCatalogueAZ',
            'getDenormalisedObjectById',
            'getObjectByFirstMatchedId',
            'getObjectClosestEntranceId',
        ]),
        isQRScannerAvailable() {
            return this.getMapConfigProp('features.qr_scanner', false) && isMobileOnly;
        },
        destinationObjectIdFromQuery(){
            return Number(this.$route.query.t) || this.$route.query.t || Number(this.$route.query.mTo);
        },
        destinationObject() {
            return this.getDenormalisedObjectById(this.destinationObjectIdFromQuery) || { id: '' };
        },
        destinationObjectId(){
            return this.destinationObject?.id;
        },
        destinationClosetEntranceId(){
            if(!this.isComponentParamsHasDestinationProps) {
                // this is fallback to save logic for old version or other map
                return this.getObjectClosestEntranceId(this.destinationObject);
            } else if(this.isComponentParamsHasDestinationProps && this.isShowDestinationClosetEntrance) {
                // if we can, we want to take ClosestEntrance from destinationObject
                if(
                    this.destinationObject?.meta?.closest_entrance 
                    || this.destinationObject?.meta?.use_default_entrance
                ) {
                    return this.getObjectClosestEntranceId(this.destinationObject)
                } else {
                    return null
                }
                //
            } else if(this.isComponentParamsHasDestinationProps && !this.isShowDestinationClosetEntrance) {
                // if we don't want show ClosestEntrance in all cases
                return null
            }
            return this.getObjectClosestEntranceId(this.destinationObject);
        },
        destinationClosetEntrance(){
            return this.getDenormalisedObjectById(this.destinationClosetEntranceId);
        },
        startObjectIdFromQuery(){
            return this.$route.query.mFrom;
        },
        startObjectId(){
            return this.startObject?.id;
        },
        startObject() {
            if(this.startObjectIdFromQuery == this.getMyPosition && this.getMyPosition ){
                return this.myPosition
            }
            return this.startObjectIdFromQuery && this.getDenormalisedObjectById(this.startObjectIdFromQuery) ||
                this.destinationClosetEntranceId && this.getDenormalisedObjectById(this.destinationClosetEntranceId);
        },
        destinationItems(){
            return [{
                name: this.t(this.destinationObject, 'name', 'Not set'),
                ...this.destinationObject
            }];
        },
        myPosition() {
            if(this.getMyPosition) {
                const positonData = cloneDeep(this.getDenormalisedObjectById(Number(this.getMyPosition)))
                positonData.name = this.$t('ui.guide.my-position', {name: this.t(positonData, 'name', 'Not set')})
                return positonData
            }
            return {}
        },
        isDisabledStart() {
            return Boolean(!this.destinationObjectId ||
                !this.startObjectId ||
                this.destinationObjectId === this.startObjectId
            );
        },
        mvGuideMode() {
            return this.guideModeCheckboxState ? EASY : FAST;
        },
        isStore() {
            return this.$route.name.includes(WEB_INTEGRATION.STORE.ROOT.NAME)
        },
        commonConfigStyles() {
            return this.getMapConfigProp('components.OPathSettings.styles') || {};
        },
        viewConfigStyles() {
            return this.getMapConfigProp(`views.${this.view}.footer.OPathSettings.styles`) || {};
        },
        componentStyles() {
            return {
                ...this.commonConfigStyles,
                ...this.viewConfigStyles
            }
        },
        commonConfigParams() {
            return this.getMapConfigProp('components.OPathSettings.params') || {};
        },
        viewConfigParams() {
            return this.getMapConfigProp(`views.${this.view}.footer.OPathSettings.params`) || {};
        },
        componentParams() {
            return {
                ...this.commonConfigParams,
                ...this.viewConfigParams
            }
        },
        isShowCloseButton() {
            return this.getMapConfigProp('components.OManageDestinationPanel.params.is_show_close_button', false)
        },
        startItemsComponent() {
            return this.commonConfigParams.start_items_component || 'OStartItemsDropdown'
        },
        isComponentParamsHasDestinationProps() {
            return this.componentParams.hasOwnProperty('should_show_destination_closet_entrance')
        },
        isShowDestinationClosetEntrance() {
            return this.componentParams.should_show_destination_closet_entrance;
        }
    },
    methods: {
        initListeners() {
            this.$bus.$on(GUIDE_NEW_POINT, this.setIdFromQrCode);
        },
        removeListeners() {
            this.$bus.$off(GUIDE_NEW_POINT, this.setIdFromQrCode);
        },
        onClickBack(isEmitClose = false) {
            const name = this.isStore ?
                WEB_INTEGRATION.STORE.INFO.NAME :
                WEB_INTEGRATION.CATALOGUE.START.NAME;
            isEmitClose && this.$bus.$emit(OBJECT_INFO_CLOSE_CLICK)
            this.$router.push({ name, query: this.$route.query }, routerPushHandler);
        },
        onStartNavigation(mvGuideMode = this.mvGuideMode) {
            const name = this.isStore ?
                WEB_INTEGRATION.STORE.PATH.NAME :
                WEB_INTEGRATION.CATALOGUE.PATH.NAME;
            this.$router.push({
                name,
                query: {
                    ...this.$route.query,
                    mFrom: this.startObjectId,
                    mTo: this.destinationObjectId,
                    mv: 'GuideView',
                    mvGuideMode
                },
                routerPushHandler,
            });
        },
        onChangeDestination(data = '') {
            const params = getPointRouterParams(this.$route, 't', data);

            this.$router.replace(params);
        },
        setIdFromQrCode(data) {
            this.isQRScanner = false;
            this.onChangeStart(data);
        },
        onChangeStart(data) {
            const params = getPointRouterParams(this.$route, 'mFrom', data);
            this.$router.replace(params);
        },
        onRunQRScanner() {
            this.isQRScanner = true;
        },
        onCloseQRScanner() {
            this.isQRScanner = false;
        }
    },
    mounted() {
        this.destinationObject &&
            this.$router.push({
                query: { 
                    ...this.$route.query, 
                    mv: this.componentParams.map_views || 'OutdoorView' 
                }
            }, routerPushHandler);
        this.initListeners();
    },
    beforeDestroy() {
        this.removeListeners();
    }
}
</script>

<style lang="scss" src="./theme.scss" />
